import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { EnrichedText } from '../../components/content/EnrichedText';
import { trainingService, type Lesson, type QuizQuestion } from '../../services/training.service';
import { getVimeoEmbedUrl } from '../../utils/video';

type Tab = 'metadata' | 'transcript' | 'quiz';

// ─── Status Badge ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'text-on-surface-variant bg-surface-container border-outline-variant/20',
    processing: 'text-tertiary bg-tertiary/10 border-tertiary/20',
    done: 'text-success bg-success/10 border-success/20',
    failed: 'text-error bg-error/10 border-error/20',
  };
  return (
    <span className={`inline-flex min-h-6 items-center justify-center rounded-full border px-2.5 text-[11px] font-semibold leading-none ${styles[status] || styles.pending}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// ─── Section Header ─────────────────────────────────────────────────────────

function SectionHeader({ icon, title }: { icon?: string; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {icon && <span className="material-symbols-outlined text-primary text-[20px]">{icon}</span>}
      <h4 className="text-sm font-semibold text-on-surface uppercase tracking-wider">{title}</h4>
    </div>
  );
}

// ─── Insight Card ───────────────────────────────────────────────────────────

function InsightCard({ tone, icon, title, children }: {
  tone: 'success' | 'caution' | 'error' | 'primary';
  icon: string;
  title: string;
  children: React.ReactNode;
}) {
  const toneMap = {
    success: 'border-l-success bg-success/5',
    caution: 'border-l-caution bg-caution/5',
    error: 'border-l-error bg-error/5',
    primary: 'border-l-primary bg-primary/5',
  };
  const iconMap = {
    success: 'text-success',
    caution: 'text-caution',
    error: 'text-error',
    primary: 'text-primary',
  };
  return (
    <div className={`rounded-xl border-l-4 p-4 ${toneMap[tone]}`}>
      <div className="flex items-start gap-3">
        <span className={`material-symbols-outlined ${iconMap[tone]} text-[20px] mt-0.5 shrink-0`}>{icon}</span>
        <div>
          <p className="text-sm font-semibold text-on-surface">{title}</p>
          <div className="text-xs text-on-surface-variant mt-1 leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Quiz Editor Components ─────────────────────────────────────────────────

function QuizEditor({
  quiz,
  onChange,
}: {
  quiz: QuizQuestion[];
  onChange: (quiz: QuizQuestion[]) => void;
}) {
  const [activeQuestion, setActiveQuestion] = useState(0);

  const current = quiz[activeQuestion];

  function updateQuestion(index: number, updater: (q: QuizQuestion) => QuizQuestion) {
    const next = quiz.map((q, i) => (i === index ? updater(q) : q));
    onChange(next);
  }

  function addQuestion() {
    onChange([
      ...quiz,
      { question: '', options: ['', '', ''], correctIndex: 0 },
    ]);
    setActiveQuestion(quiz.length);
  }

  function removeQuestion(index: number) {
    const next = quiz.filter((_, i) => i !== index);
    onChange(next);
    setActiveQuestion(Math.min(activeQuestion, Math.max(0, next.length - 1)));
  }

  function moveQuestion(index: number, direction: -1 | 1) {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= quiz.length) return;
    const next = [...quiz];
    [next[index], next[newIndex]] = [next[newIndex], next[index]];
    onChange(next);
    setActiveQuestion(newIndex);
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Question List */}
      <div className="lg:col-span-1 bg-surface-container-low rounded-2xl border border-outline-variant/10 p-4 space-y-2 max-h-[600px] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-on-surface">Questions</h3>
          <button
            onClick={addQuestion}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary/10 text-primary text-xs font-bold hover:bg-primary/20 transition-colors"
          >
            <span className="material-symbols-outlined text-[16px]">add</span>
            Add
          </button>
        </div>
        {quiz.length === 0 ? (
          <p className="text-xs text-on-surface-variant text-center py-6">No questions yet. Click Add to create one.</p>
        ) : (
          quiz.map((q, i) => (
            <button
              key={i}
              onClick={() => setActiveQuestion(i)}
              className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-colors ${
                activeQuestion === i
                  ? 'bg-primary/10 text-primary border border-primary/20'
                  : 'bg-surface-container text-on-surface hover:bg-surface-container-high border border-transparent'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${
                  activeQuestion === i ? 'bg-primary text-on-primary' : 'bg-surface-container-high text-on-surface-variant'
                }`}>
                  {i + 1}
                </span>
                <span className="truncate flex-1">{q.question || 'Untitled question'}</span>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Question Editor */}
      <div className="lg:col-span-2 bg-surface-container-low rounded-2xl border border-outline-variant/10 p-6 space-y-5">
        {current ? (
          <>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-on-surface">Question {activeQuestion + 1}</h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => moveQuestion(activeQuestion, -1)}
                  disabled={activeQuestion === 0}
                  className="p-1.5 rounded-lg hover:bg-surface-container text-on-surface-variant disabled:opacity-30"
                >
                  <span className="material-symbols-outlined text-[18px]">arrow_upward</span>
                </button>
                <button
                  onClick={() => moveQuestion(activeQuestion, 1)}
                  disabled={activeQuestion >= quiz.length - 1}
                  className="p-1.5 rounded-lg hover:bg-surface-container text-on-surface-variant disabled:opacity-30"
                >
                  <span className="material-symbols-outlined text-[18px]">arrow_downward</span>
                </button>
                <button
                  onClick={() => removeQuestion(activeQuestion)}
                  className="p-1.5 rounded-lg hover:bg-error/10 text-on-surface-variant hover:text-error transition-colors"
                >
                  <span className="material-symbols-outlined text-[18px]">delete</span>
                </button>
              </div>
            </div>

            <label className="block">
              <span className="text-xs text-on-surface-variant font-medium">Question text</span>
              <input
                value={current.question}
                onChange={(e) => updateQuestion(activeQuestion, (q) => ({ ...q, question: e.target.value }))}
                placeholder="e.g., What is the main purpose of a React hook?"
                className="mt-1 w-full bg-surface-container rounded-xl px-4 py-3 text-sm text-on-surface border border-outline-variant/20 focus:border-primary/40 focus:outline-none transition-colors"
              />
            </label>

            <div className="space-y-2">
              <span className="text-xs text-on-surface-variant font-medium">Answer options</span>
              {current.options.map((opt, oi) => (
                <div key={oi} className="flex items-center gap-3">
                  <button
                    onClick={() => updateQuestion(activeQuestion, (q) => ({ ...q, correctIndex: oi }))}
                    className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      current.correctIndex === oi
                        ? 'bg-success text-on-primary'
                        : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high border border-outline-variant/20'
                    }`}
                    title={current.correctIndex === oi ? 'Correct answer' : 'Mark as correct'}
                  >
                    <span className="material-symbols-outlined text-[16px]">
                      {current.correctIndex === oi ? 'check' : 'radio_button_unchecked'}
                    </span>
                  </button>
                  <input
                    value={opt}
                    onChange={(e) => updateQuestion(activeQuestion, (q) => ({
                      ...q,
                      options: q.options.map((o, i) => (i === oi ? e.target.value : o)),
                    }))}
                    placeholder={`Option ${oi + 1}`}
                    className="flex-1 bg-surface-container rounded-xl px-4 py-2.5 text-sm text-on-surface border border-outline-variant/20 focus:border-primary/40 focus:outline-none transition-colors"
                  />
                  {current.options.length > 2 && (
                    <button
                      onClick={() => updateQuestion(activeQuestion, (q) => ({
                        ...q,
                        options: q.options.filter((_, i) => i !== oi),
                        correctIndex: q.correctIndex >= oi && q.correctIndex > 0 ? q.correctIndex - 1 : q.correctIndex,
                      }))}
                      className="p-1.5 rounded-lg hover:bg-error/10 text-on-surface-variant hover:text-error transition-colors"
                    >
                      <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                  )}
                </div>
              ))}
              {current.options.length < 6 && (
                <button
                  onClick={() => updateQuestion(activeQuestion, (q) => ({ ...q, options: [...q.options, ''] }))}
                  className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors mt-2"
                >
                  <span className="material-symbols-outlined text-[16px]">add</span>
                  Add option
                </button>
              )}
            </div>

            {current.correctIndex >= 0 && current.correctIndex < current.options.length && current.options[current.correctIndex] && (
              <div className="bg-success/5 border border-success/20 rounded-xl p-3">
                <p className="text-xs text-success font-semibold flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[16px]">check_circle</span>
                  Correct answer: {current.options[current.correctIndex]}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <span className="material-symbols-outlined text-4xl text-on-surface-variant/30 block mb-2">quiz</span>
            <p className="text-on-surface-variant text-sm">No questions yet.</p>
            <button
              onClick={addQuestion}
              className="mt-3 px-4 py-2 rounded-lg bg-primary text-on-primary text-xs font-bold"
            >
              Add first question
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function AdminLessonEdit() {
  const { id: trainingId, lessonId } = useParams<{ id: string; lessonId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('metadata');
  const [metaForm, setMetaForm] = useState({ title: '', description: '', videoUrl: '', order: 0, isActive: true });
  const [quiz, setQuiz] = useState<QuizQuestion[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: lesson, isLoading } = useQuery({
    queryKey: ['admin-lesson', lessonId],
    queryFn: () => trainingService.adminGetLesson(lessonId!),
    enabled: !!lessonId,
    refetchInterval: (query) => {
      const data = query.state.data as (Lesson & { videoUrl?: string }) | undefined;
      const fetchCount = query.state.dataUpdateCount ?? 0;
      if (data?.status === 'processing' && fetchCount < 60) return 3000;
      return false;
    },
  });

  const { data: training } = useQuery({
    queryKey: ['admin-training', trainingId],
    queryFn: () => trainingService.adminGet(trainingId!),
    enabled: !!trainingId,
  });

  useEffect(() => {
    if (lesson) {
      setMetaForm({
        title: lesson.title,
        description: lesson.description || '',
        videoUrl: lesson.videoUrl || '',
        order: lesson.order,
        isActive: lesson.isActive ?? true,
      });
      setQuiz(lesson.quiz && Array.isArray(lesson.quiz) ? lesson.quiz : []);
    }
  }, [lesson?.id]);

  const updateMetaMutation = useMutation({
    mutationFn: (data: Partial<Lesson>) => trainingService.adminUpdateLesson(lessonId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-lesson', lessonId] });
      queryClient.invalidateQueries({ queryKey: ['admin-training', trainingId] });
    },
  });

  async function handleFileUpload(file: File) {
    if (!lessonId) return;
    setUploading(true);
    setUploadStatus('Uploading video to Vimeo...');
    try {
      await trainingService.adminUploadVideo(lessonId, file);
      setUploadStatus('Processing started! AI is generating transcript, description, and quiz.');
      queryClient.invalidateQueries({ queryKey: ['admin-lesson', lessonId] });
    } catch (err) {
      setUploadStatus(`Error: ${(err as Error).message}`);
    } finally {
      setUploading(false);
    }
  }

  function handleSaveMetadata() {
    updateMetaMutation.mutate({
      title: metaForm.title,
      description: metaForm.description,
      videoUrl: metaForm.videoUrl,
      order: metaForm.order,
      isActive: metaForm.isActive,
    });
  }

  function handleSaveQuiz() {
    updateMetaMutation.mutate({ quiz });
  }

  if (isLoading) {
    return (
      <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto space-y-8">
        <div className="h-6 w-48 bg-surface-container-high rounded animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-80 bg-surface-container-high rounded-2xl animate-pulse" />
          <div className="h-80 bg-surface-container-high rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto">
        <div className="text-center py-20">
          <span className="material-symbols-outlined text-5xl text-on-surface-variant/30 block mb-3">error</span>
          <p className="text-on-surface font-semibold">Video not found</p>
          <button onClick={() => navigate(`/admin/trainings/${trainingId}`)} className="mt-4 text-primary text-sm font-medium hover:underline">
            Back to module
          </button>
        </div>
      </div>
    );
  }

  const hasAiContent = lesson.status === 'done' && (lesson.transcript || lesson.description || lesson.quiz);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto space-y-8 md:space-y-10">
      {/* Breadcrumb + Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-xs text-on-surface-variant mb-1">
          <button onClick={() => navigate('/admin/trainings')} className="hover:text-on-surface transition-colors">Modules</button>
          <span>/</span>
          <button onClick={() => navigate(`/admin/trainings/${trainingId}`)} className="hover:text-on-surface transition-colors truncate max-w-[200px]">{training?.title}</button>
          <span>/</span>
          <span className="text-on-surface truncate max-w-[200px]">{lesson.title}</span>
        </div>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-on-surface">{lesson.title}</h1>
          <div className="flex items-center gap-2">
            {lesson.status && <StatusBadge status={lesson.status} />}
            {lesson.isActive ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-success bg-success/10 px-2.5 py-1 rounded-full border border-success/20">
                <span className="material-symbols-outlined shrink-0 self-center leading-none text-[14px]">visibility</span> Active
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-on-surface-variant bg-surface-container px-2.5 py-1 rounded-full border border-outline-variant/20">
                <span className="material-symbols-outlined shrink-0 self-center leading-none text-[14px]">visibility_off</span> Inactive
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Video Preview + Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Video Player / Upload Area */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-surface-container-low rounded-2xl border border-outline-variant/10 overflow-hidden">
            {lesson.videoUrl || (lesson as { videoUrl?: string }).videoUrl ? (
              (() => {
                const url = (lesson as { videoUrl?: string }).videoUrl || lesson.videoUrl || '';
                const vimeoEmbedUrl = getVimeoEmbedUrl(url, { color: 'c0c1ff' });
                return vimeoEmbedUrl ? (
                  <iframe
                    src={vimeoEmbedUrl}
                    className="w-full aspect-video bg-surface-container"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video src={url} controls className="w-full aspect-video bg-surface-container" />
                );
              })()
            ) : (
              <div className="aspect-video bg-surface-container flex flex-col items-center justify-center gap-3">
                <div className="w-16 h-16 rounded-2xl bg-surface-container-high flex items-center justify-center">
                  <span className="material-symbols-outlined shrink-0 self-center leading-none text-3xl text-on-surface-variant/40">cloud_upload</span>
                </div>
                <p className="text-on-surface-variant text-sm">No video uploaded yet</p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl primary-gradient text-on-primary text-sm font-bold shadow-lg shadow-primary/15 hover:shadow-primary/25 hover:-translate-y-0.5 transition-all disabled:opacity-50"
                >
                  <span className="material-symbols-outlined shrink-0 self-center leading-none text-lg">upload</span>
                  {uploading ? 'Uploading...' : 'Upload Video'}
                </button>
                {uploadStatus && (
                  <p className={`text-xs ${uploadStatus.startsWith('Error') ? 'text-error' : 'text-on-surface-variant'}`}>{uploadStatus}</p>
                )}
              </div>
            )}
          </div>

          {(lesson.videoUrl || (lesson as { videoUrl?: string }).videoUrl) && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || lesson.status === 'processing'}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-surface-container text-sm font-medium text-on-surface-variant hover:bg-surface-container-high transition-colors disabled:opacity-50"
              >
                <span className="material-symbols-outlined shrink-0 self-center leading-none text-lg">upload</span>
                {uploading ? 'Uploading...' : 'Replace Video'}
              </button>
              {lesson.status === 'processing' && (
                <span className="flex items-center gap-1.5 text-xs text-tertiary">
                  <span className="material-symbols-outlined text-sm animate-spin" style={{ animationDuration: '2s' }}>sync</span>
                  AI is processing this video...
                </span>
              )}
              {lesson.status === 'failed' && lesson.errorMessage && (
                <span className="text-xs text-error">{lesson.errorMessage}</span>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileUpload(file);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
            className="hidden"
          />
        </div>

        {/* Status Sidebar */}
        <div className="space-y-4">
          {lesson.status === 'processing' && (
            <InsightCard tone="caution" icon="sync" title="AI Processing">
              Transcript, description, and quiz are being generated. This may take a few minutes depending on video length.
            </InsightCard>
          )}

          {lesson.status === 'failed' && (
            <InsightCard tone="error" icon="error" title="Processing Failed">
              {lesson.errorMessage || 'Something went wrong during AI processing. Try uploading again.'}
            </InsightCard>
          )}

          {lesson.status === 'done' && (
            <InsightCard tone="success" icon="check_circle" title="AI Ready">
              Transcript, description, and quiz have been generated. Review and edit them below.
            </InsightCard>
          )}

          <div className="bg-surface-container-low rounded-2xl border border-outline-variant/10 p-5 space-y-4">
            <SectionHeader icon="analytics" title="Quick Stats" />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-on-surface-variant">Duration</span>
                <span className="text-sm font-semibold text-on-surface">
                  {lesson.duration > 0 ? `${Math.floor(lesson.duration / 60)}m ${lesson.duration % 60}s` : '—'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-on-surface-variant">Order</span>
                <span className="text-sm font-semibold text-on-surface">{lesson.order}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-on-surface-variant">Quiz Questions</span>
                <span className="text-sm font-semibold text-on-surface">
                  {quiz.length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-on-surface-variant">Transcript</span>
                <span className="text-sm font-semibold text-on-surface">
                  {lesson.transcript ? `${lesson.transcript.length.toLocaleString()} chars` : '—'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-surface-container-low rounded-xl p-1 border border-outline-variant/10 w-fit">
        {(['metadata', 'transcript', 'quiz'] as Tab[]).map((t) => {
          const disabled = t !== 'metadata' && !hasAiContent && lesson.status !== 'done';
          return (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              disabled={disabled}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                activeTab === t
                  ? 'bg-surface-container-high text-on-surface shadow-sm'
                  : disabled
                    ? 'text-on-surface-variant/30 cursor-not-allowed'
                    : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {t}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {activeTab === 'metadata' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-surface-container-low rounded-2xl border border-outline-variant/10 p-6 space-y-4">
              <SectionHeader icon="edit" title="Edit Metadata" />
              <div className="space-y-3">
                <label className="block">
                  <span className="text-xs text-on-surface-variant font-medium">Title</span>
                  <input
                    value={metaForm.title}
                    onChange={(e) => setMetaForm((f) => ({ ...f, title: e.target.value }))}
                    className="mt-1 w-full bg-surface-container rounded-xl px-4 py-3 text-sm text-on-surface border border-outline-variant/20 focus:border-primary/40 focus:outline-none transition-colors"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-on-surface-variant font-medium">Description / Overview</span>
                  <p className="text-[11px] text-on-surface-variant/60 mt-0.5">
                    Auto-generated by AI from the transcript. Supports enriched text like headings, bullets, bold, and paragraphs.
                  </p>
                  <textarea
                    value={metaForm.description}
                    onChange={(e) => setMetaForm((f) => ({ ...f, description: e.target.value }))}
                    className="mt-1 w-full bg-surface-container rounded-xl px-4 py-3 text-sm text-on-surface border border-outline-variant/20 focus:border-primary/40 focus:outline-none transition-colors resize-none"
                    rows={6}
                  />
                  <p className="text-[11px] text-on-surface-variant/60">
                    Example: `## Key takeaways`, `- first point`, `**important**`
                  </p>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs text-on-surface-variant font-medium">Order</span>
                    <input
                      type="number"
                      value={metaForm.order}
                      onChange={(e) => setMetaForm((f) => ({ ...f, order: Number(e.target.value) }))}
                      className="mt-1 w-full bg-surface-container rounded-xl px-4 py-3 text-sm text-on-surface border border-outline-variant/20 focus:border-primary/40 focus:outline-none transition-colors"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm text-on-surface-variant pt-6">
                    <input
                      type="checkbox"
                      checked={metaForm.isActive}
                      onChange={(e) => setMetaForm((f) => ({ ...f, isActive: e.target.checked }))}
                      className="accent-primary w-4 h-4 rounded"
                    />
                    Active (visible to students)
                  </label>
                </div>
              </div>
              <button
                onClick={handleSaveMetadata}
                disabled={!metaForm.title.trim() || updateMetaMutation.isPending}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl primary-gradient text-on-primary text-sm font-bold shadow-lg shadow-primary/15 hover:shadow-primary/25 hover:-translate-y-0.5 transition-all disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-lg">save</span>
                {updateMetaMutation.isPending ? 'Saving...' : 'Save Metadata'}
              </button>
            </div>

            <div className="bg-surface-container-low rounded-2xl border border-outline-variant/10 p-6 space-y-4">
              <SectionHeader icon="info" title="Description Preview" />
              {metaForm.description.trim() ? (
                <div className="rounded-xl border border-outline-variant/10 bg-surface-container p-4">
                  <EnrichedText content={metaForm.description} />
                </div>
              ) : (
                <p className="text-xs text-on-surface-variant leading-relaxed">
                  Upload a video file to automatically generate transcript, description, and quiz using AI. Duration is auto-detected from the video.
                </p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'transcript' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-surface-container-low rounded-2xl border border-outline-variant/10 p-6">
              <SectionHeader title="Transcript" />
              {lesson.transcriptTimeline && Array.isArray(lesson.transcriptTimeline) && lesson.transcriptTimeline.length > 0 ? (
                <div className="max-h-[600px] overflow-y-auto space-y-1 pr-2">
                  {lesson.transcriptTimeline.map((entry, i) => (
                    <div key={i} className="flex gap-3 px-3 py-2 rounded-lg hover:bg-surface-container transition-colors">
                      <span className="text-xs text-primary font-mono shrink-0 mt-0.5">{entry.startLabel}</span>
                      <div className="flex flex-1 items-start gap-2 min-w-0">
                        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                          {String(entry.speaker ?? '?').charAt(0).toUpperCase()}
                        </span>
                        <p className="min-w-0 text-sm text-on-surface-variant">{entry.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : lesson.transcript ? (
                <div className="max-h-[600px] overflow-y-auto">
                  <p className="text-sm text-on-surface-variant whitespace-pre-wrap leading-relaxed">{lesson.transcript}</p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <span className="material-symbols-outlined text-4xl text-on-surface-variant/30 block mb-2">transcript</span>
                  <p className="text-on-surface-variant text-sm">No transcript available yet.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'quiz' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <SectionHeader icon="quiz" title="Quiz Editor" />
                <p className="text-xs text-on-surface-variant -mt-1">
                  Students must score 70% or higher to mark this video as complete.
                </p>
              </div>
              <button
                onClick={handleSaveQuiz}
                disabled={updateMetaMutation.isPending}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl primary-gradient text-on-primary text-sm font-bold shadow-lg shadow-primary/15 hover:shadow-primary/25 hover:-translate-y-0.5 transition-all disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-lg">save</span>
                {updateMetaMutation.isPending ? 'Saving...' : 'Save Quiz'}
              </button>
            </div>
            <QuizEditor quiz={quiz} onChange={setQuiz} />
          </div>
        )}
      </div>
    </div>
  );
}
